import { SearchDatasArray } from '../../types/Search';

export const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface SearchCacheState {
    cache: Record<string, { data: SearchDatasArray; timestamp: number }>;
}

const initialState: SearchCacheState = {
    cache: {},
};

export const SEARCH_CACHE_ADD = 'SEARCH_CACHE_ADD';
export const SEARCH_CACHE_CLEAR = 'SEARCH_CACHE_CLEAR';
export const SEARCH_CACHE_REMOVE = 'SEARCH_CACHE_REMOVE';

interface AddToCache {
    type: typeof SEARCH_CACHE_ADD;
    payload: {
        query: string;
        data: SearchDatasArray;
    };
}

interface ClearCache {
    type: typeof SEARCH_CACHE_CLEAR;
}

interface RemoveFromCache {
    type: typeof SEARCH_CACHE_REMOVE;
    payload: string;
}

type SearchCacheTypes = AddToCache | ClearCache | RemoveFromCache;

// eslint-disable-next-line default-param-last
export const searchReducer = (state = initialState, action: SearchCacheTypes): SearchCacheState => {
    switch (action.type) {
        case SEARCH_CACHE_ADD:
            return {
                ...state,
                cache: {
                    ...state.cache,
                    [action.payload.query]: {
                        data: action.payload.data,
                        timestamp: Date.now(),
                    },
                },
            };
        case SEARCH_CACHE_CLEAR:
            return {
                ...state,
                cache: {},
            };
        case SEARCH_CACHE_REMOVE: {
            const newCache = { ...state.cache };
            delete newCache[action.payload];

            return {
                ...state,
                cache: newCache,
            };
        }
        default:
            return state;
    }
};

export const addToSearchCache = (query: string, data: SearchDatasArray): AddToCache => ({
    type: SEARCH_CACHE_ADD,
    payload: { query, data },
});

export const clearSearchCache = (): ClearCache => ({
    type: SEARCH_CACHE_CLEAR,
});

export const removeFromSearchCache = (query: string): RemoveFromCache => ({
    type: SEARCH_CACHE_REMOVE,
    payload: query,
});

export const isCacheValid = (timestamp: number): boolean => Date.now() - timestamp < CACHE_DURATION;

export const hasCacheExpired = (cache: Record<string, { data: SearchDatasArray; timestamp: number }>): boolean => {
    const now = Date.now();
    return Object.values(cache).some((entry) => now - entry.timestamp > CACHE_DURATION);
};

export const getSearchCache = (state: { searchStore: SearchCacheState }, query: string) => {
    const cacheEntry = state.searchStore.cache[query];

    if (!cacheEntry) return null;

    if (!isCacheValid(cacheEntry.timestamp)) {
        return null;
    }

    return cacheEntry.data;
};
